import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = [
    "list",
    "row",
    "optionInput",
    "correctToggle",
    "addButton",
    "questionInput",
    "maxAttemptsInput",
  ];
  static values = {
    updateUrl: String,
    multipleCorrect: Boolean,
    min: { type: Number, default: 2 },
    max: { type: Number, default: 8 },
    debounceMs: { type: Number, default: 400 },
  };

  connect() {
    this._refreshAddVisibility();
  }

  disconnect() {
    clearTimeout(this._saveTimer);
  }

  scheduleSave() {
    this._scheduleSave();
  }

  optionTyped(event) {
    const row = event.target.closest("[data-choice-quiz-editor-target='row']");
    const toggle = row?.querySelector("[data-choice-quiz-editor-target='correctToggle']");
    if (toggle) toggle.value = event.target.value;
    this._scheduleSave();
  }

  enterToAdd(event) {
    event.preventDefault();
    if (this._optionCount() < this.maxValue) {
      this.add();
    }
  }

  correctChanged(event) {
    if (!this.multipleCorrectValue) {
      // Radio behavior: ensure only one is checked at a time.
      this.correctToggleTargets.forEach((toggle) => {
        if (toggle !== event.target) toggle.checked = false;
      });
    }
    this._scheduleSave();
  }

  add() {
    if (this._optionCount() >= this.maxValue) return;

    const tpl = document.createElement("li");
    tpl.className = "flex items-center gap-2";
    tpl.setAttribute("data-choice-quiz-editor-target", "row");
    const inputType = this.multipleCorrectValue ? "checkbox" : "radio";
    const inputName = this.multipleCorrectValue ? "exercise[correct][]" : "exercise[correct]";
    const inputClasses = [
      "w-full rounded-ui border transition-colors duration-150",
      "placeholder:text-content-muted",
      "focus:outline-none focus:ring-1",
      "px-3 py-1.5 text-sm",
      "border-surface-border focus:border-brand-500 focus:ring-brand-500",
      "hover:border-surface-border-strong",
    ].join(" ");
    tpl.innerHTML = `
      <input type="${inputType}" name="${inputName}" value="" class="shrink-0"
             data-choice-quiz-editor-target="correctToggle"
             data-action="change->choice-quiz-editor#correctChanged">
      <div class="flex-1 min-w-0">
        <input type="text" value=""
               autocomplete="off"
               data-choice-quiz-editor-target="optionInput"
               data-action="input->choice-quiz-editor#optionTyped keydown.enter->choice-quiz-editor#enterToAdd"
               class="${inputClasses}">
      </div>
      <button type="button"
              data-action="click->choice-quiz-editor#remove"
              class="shrink-0 text-content-muted hover:text-error">×</button>
    `;
    this.listTarget.appendChild(tpl);
    tpl.querySelector("input[type=text]")?.focus();
    this._refreshAddVisibility();
  }

  remove(event) {
    const row = event.target.closest("[data-choice-quiz-editor-target='row']");
    if (!row) return;
    row.remove();
    this._refreshAddVisibility();
    this._scheduleSave();
  }

  _refreshAddVisibility() {
    if (!this.hasAddButtonTarget) return;
    this.addButtonTarget.classList.toggle("hidden", this._optionCount() >= this.maxValue);
  }

  _optionCount() {
    return this.rowTargets.length;
  }

  _scheduleSave() {
    clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => this._save(), this.debounceMsValue);
  }

  async _save() {
    if (!this.hasUpdateUrlValue) return;

    const token = document.querySelector("meta[name=csrf-token]")?.content || "";
    const formData = new FormData();
    formData.append("_method", "patch");

    if (this.hasQuestionInputTarget) {
      formData.append("exercise[question]", this.questionInputTarget.value);
    }

    const options = this.optionInputTargets.map((i) => i.value.trim()).filter((v) => v.length > 0);
    if (options.length === 0) {
      formData.append("exercise[options][]", "");
    } else {
      options.forEach((o) => formData.append("exercise[options][]", o));
    }

    const correctValues = this.correctToggleTargets
      .filter((t) => t.checked)
      .map((t) => {
        const row = t.closest("[data-choice-quiz-editor-target='row']");
        return row?.querySelector("input[type=text]")?.value.trim() || "";
      })
      .filter((v) => v.length > 0);

    if (correctValues.length === 0) {
      formData.append("exercise[correct][]", "");
    } else {
      correctValues.forEach((v) => formData.append("exercise[correct][]", v));
    }

    if (this.hasMaxAttemptsInputTarget) {
      formData.append("exercise[max_attempts]", this.maxAttemptsInputTarget.value);
    }

    const response = await fetch(this.updateUrlValue, {
      method: "POST",
      headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": token },
      body: formData,
    });
    if (!response.ok) {
      const html = await response.text();
      if (html) Turbo.renderStreamMessage(html);
    }
  }
}
